import React from 'react';
import { motion } from 'framer-motion';
import './LegalPage.css';

function TermsOfService() {
  const isIOSSafari = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-safari');
  const inViewViewport = isIOSSafari ? { once: true, amount: 0.25 } : { once: true };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <main className="legal-page">
      <motion.div
        className="policy-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container">
          <h1 className="legal-title">Terms of Service</h1>
          <p className="policy-dates">
            Effective Date: August 27, 2025
          </p>
        </div>
      </motion.div>

      <motion.div
        className="container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={inViewViewport}
      >
        <article className="legal-card">
          <motion.section variants={itemVariants} className="legal-section">
            <p className="intro-text">
              These Terms of Service ("Terms") govern your use of Versa Launch ("we," "our," "us"), a division of Versa Logix LLC, including our website <a href="https://versalaunch.com">versalaunch.com</a>, apps, and software development services (the "Services"). By accessing or using our Services, you agree to these Terms.
            </p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>1. Services Provided</h2>
            <p>Versa Launch designs and develops software, websites, mobile applications, and digital solutions. Additional services may include hosting, consulting, and ongoing support.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>2. Eligibility</h2>
            <p>You must be at least 18 years old (or the legal age of majority in your region) to use our Services.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>3. User Responsibilities</h2>
            <p>By using our Services, you agree to:</p>
            <ul>
              <li>Provide accurate information.</li>
              <li>Not misuse our Services (hacking, unlawful use, spamming, etc.).</li>
              <li>Respect intellectual property and third-party rights.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>4. Payments & Refunds</h2>
            <ul>
              <li>Fees for Services are due as agreed in contracts or invoices.</li>
              <li>Refunds are only issued in cases where required by law or stated in your service agreement.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>5. Intellectual Property</h2>
            <ul>
              <li>All content, logos, and materials created by Versa Launch remain the property of Versa Logix LLC, unless otherwise agreed in writing.</li>
              <li>Clients retain rights to their own pre-existing materials provided for use.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>6. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <ul>
              <li>Versa Launch and Versa Logix LLC are not liable for indirect, incidental, or consequential damages arising from use of our Services.</li>
              <li>We do not guarantee that Services will be error-free, uninterrupted, or suitable for every purpose.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>7. Termination</h2>
            <p>We may suspend or terminate Services if you violate these Terms or misuse our platform.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>8. Governing Law</h2>
            <p>These Terms are governed by the laws of the State of Florida, USA.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>9. Changes to Terms</h2>
            <p>We may update these Terms at any time. Continued use of Services after updates means you accept the revised Terms.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>10. 10DLC Messaging & Compliance</h2>
            <p>If you use Versa Launch Services to send SMS or MMS messages through Application-to-Person (A2P) 10DLC messaging channels:</p>
            <ul>
              <li><strong>Registration:</strong> You must complete required 10DLC registration, including brand and campaign approval, before sending messages. Failure to do so may result in message blocking or carrier penalties.</li>
              <li><strong>Content Restrictions:</strong> You may not send messages containing prohibited content (including but not limited to SHAFT categories: sex, hate, alcohol, firearms, tobacco, controlled substances, or other carrier-restricted topics).</li>
              <li><strong>Consent:</strong> You are responsible for obtaining proper consumer consent (opt-in) before sending messages. You must also provide clear opt-out instructions (e.g., "Reply STOP to unsubscribe").</li>
              <li><strong>Throughput & Delivery:</strong> Message delivery and throughput are subject to carrier restrictions. Versa Launch does not guarantee delivery times or success rates.</li>
              <li><strong>Fines & Penalties:</strong> You are responsible for any carrier fines, penalties, or fees incurred due to misuse, unregistered traffic, or non-compliance.</li>
              <li><strong>Termination for Violations:</strong> We may suspend or terminate messaging access if you violate carrier rules, regulatory requirements, or these Terms.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>11. Contact</h2>
            <p>For questions regarding these Terms, contact us:</p>
            <div className="contact-box">
              <p><strong>Versa Launch, a division of Versa Logix LLC</strong></p>
              <p>Ponte Vedra Beach, Florida</p>
              <p>Email: <a href="mailto:support@versalaunch.com">support@versalaunch.com</a></p>
              <p>Phone: <a href="tel:19047504885">+1 (904) 750-4885</a></p>
            </div>
          </motion.section>
        </article>
      </motion.div>
    </main>
  );
}

export default TermsOfService;
